import { DateTime } from 'luxon'
import React, { FC, useCallback, useState } from 'react'
import CheckoutContextProvider, { ICheckoutContext } from './checkout.context'

// types
import { useTranslation } from 'react-i18next'
import { SESSION_STORAGE_setCheckout } from '../../sessionStorage/checkoutData.sessionStorage'
import { Checkout } from '../../types/checkout.interface'
import { Lot, LotViewName } from '../../types/lot.interface'
import { Vehicle } from '../../types/vehicle.interface'
import useLanguage from '@hooks/useLanguage.hook'

export const CheckoutContextContainer: FC = ({ children }) => {
    const [lng] = useLanguage()
    const [checkout, setCheckout] = useState<Checkout | null>(null)
    const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([])
    const [email, setEmail] = useState<string>('')

    const modifyAndSetCheckout = useCallback(
        (newCheckout: Checkout) => {
            SESSION_STORAGE_setCheckout({
                ...newCheckout,
                locale: lng
            })
            setCheckout(newCheckout)
        },
        [lng]
    )

    const modifyAndSetHourlyCheckout = useCallback((lot: Lot, price: number, duration: number) => {
        setCheckout({
            lot,
            price,
            duration,
            start_datetime: DateTime.now(),
            end_datetime: DateTime.now().plus({ hours: duration }),
            selectedVehicles: [],
            email: '',
            lotCheckoutType: LotViewName.HOURLY
        })
        setSelectedVehicles([])
        setEmail('')
    }, [])

    const modifyAndSetDailyCheckout = useCallback(
        (lot: Lot, price: number, start_datetime: DateTime, end_datetime: DateTime) => {
            setCheckout({
                lot,
                price,
                start_datetime,
                end_datetime,
                selectedVehicles: [],
                email: '',
                lotCheckoutType: LotViewName.DAILY
            })
            setSelectedVehicles([])
            setEmail('')
        },
        []
    )

    const modifyAndSetNightlyCheckout = useCallback(
        (lot: Lot, price: number, start_datetime: DateTime, end_datetime: DateTime) => {
            setCheckout({
                lot,
                price,
                start_datetime,
                end_datetime,
                selectedVehicles: [],
                email: '',
                lotCheckoutType: LotViewName.NIGHTLY
            })
            setSelectedVehicles([])
            setEmail('')
        },
        []
    )

    const modifyAndSetSubscriptionCheckout = useCallback((lot: Lot, price: number, start_datetime: DateTime) => {
        setCheckout({
            lot,
            price,
            start_datetime,
            selectedVehicles: [],
            email: '',
            lotCheckoutType: LotViewName.SUBSCRIPTION
        })
        setSelectedVehicles([])
        setEmail('')
    }, [])

    const context: ICheckoutContext = {
        checkout,
        selectedVehicles,
        email,
        modifyAndSetCheckout,
        modifyAndSetHourlyCheckout,
        modifyAndSetDailyCheckout,
        modifyAndSetNightlyCheckout,
        modifyAndSetSubscriptionCheckout,
        setCheckout,
        setSelectedVehicles,
        setEmail
    }
    return <CheckoutContextProvider context={context}>{children}</CheckoutContextProvider>
}
